import { LoggerService } from '@danangelus/ngx-app-core'

const log = LoggerService.get('RetryUtils')

export interface RetryOptions<T> {
    maxRetries?: number
    delayMs?: number
    shouldRetry?: (result: T) => boolean
    label?: string
}

/**
 * Executes an async function with retry logic for transient failures.
 * Uses linear backoff (delayMs * attempt) between retries.
 *
 * @param fn The async function to execute
 * @param options Configuration for retry behaviour
 * @returns The result of the function call
 */
export async function withRetry<T>(
    fn: () => Promise<T>,
    options: RetryOptions<T> = {}
): Promise<T> {
    const { maxRetries = 3, delayMs = 1000, shouldRetry, label = 'request' } = options

    let lastResult: T
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        lastResult = await fn()

        if (!shouldRetry || !shouldRetry(lastResult)) {
            return lastResult
        }

        if (attempt < maxRetries) {
            const delay = delayMs * attempt
            log.warn(`[${label}] Attempt ${attempt}/${maxRetries} received a retryable response. Retrying in ${delay}ms...`)
            await new Promise(resolve => setTimeout(resolve, delay))
        }
    }

    log.error(`[${label}] All ${maxRetries} attempts failed. Returning last result.`)
    return lastResult!
}

export function isServerError(response: any): boolean {
    return response?.status >= 500 || response?.status === 0
}
